import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import { ReactElement, useState } from 'react';
import ActionButton from 'src/components/ActionButton/ActionButton';
import FormDropdown from 'src/components/FormInputs/FormDropdown/FormDropdown';
import FormInputLabel from 'src/components/FormInputs/FormInputLabel/FormInputLabel';
import Modal from 'src/components/Modal/Modal';
import { useAppSelector } from 'src/redux/hooks';
import { CourseCodeFormViewModel } from 'src/types/CourseCodeFormViewModel';
import { CourseCodeModel } from 'src/types/CourseCodeModel';
import { DialogType } from 'src/types/DialogTypes';
import FormRadioList from '../../../components/FormInputs/FormRadioList/FormRadioList';
import Panel from '../../../components/Panel/Panel';
import { selectCourseCodeFormViewModel } from '../../../redux/slices/courseCodeSlice';
import { PanelType } from '../../../types/PanelType';
import { FormRadioListOrientation } from '../../../types/propTypes/FormRadioListPropTypes';
import { CourseCodeValidationSchema } from '../../../types/Validation/CourseCodeValidationSchema';
import { isStateAdmin } from '../../../utilities/userUtilities';

const CourseCodeCertificationAndFundingPanel = (): ReactElement => {
  const formViewModel: CourseCodeFormViewModel = useAppSelector(
    selectCourseCodeFormViewModel
  );

  const { values } = useFormikContext<CourseCodeModel>();
  const [deleteIndex, setDeleteIndex] = useState(-1);

  return (
    <Panel panelType={PanelType.INFO} heading="Certification">
      <div className="field-row">
        <FormRadioList
          displayName="License Required"
          field="certificateRequired"
          options={formViewModel.certificateRequiredOptions}
          optionOrientation={FormRadioListOrientation.Horizontal}
          validationSchema={CourseCodeValidationSchema}
          disabled={!isStateAdmin()}
        />
      </div>

      <div>
        <FormInputLabel
          field="specificEndrosementsList"
          displayName="Specific Endorsements"
        />
        <FieldArray name="specificEndorsementsList">
          {(fieldArrayProps: FieldArrayRenderProps): ReactElement => {
            const { push, remove } = fieldArrayProps;

            return (
              <>
                <div>
                  {values.specificEndorsementsList.map((_, index: number) => (
                    <div
                      key={index}
                      className="field-row-specific-endrosement "
                    >
                      <div>
                        <br />
                        <FormDropdown
                          displayName="Add specific endorsement combinations"
                          field={`specificEndorsementsList[${index}].endorsements`}
                          name={`specificEndorsementsList[${index}].endorsements`}
                          isMulti={true}
                          options={formViewModel.specificEndorsementOptions.map(
                            (option) => ({
                              ...option,
                              text: `${option.value} ${option.text}`,
                            })
                          )}
                          containerClasses="specific-endorsement-container"
                          validationSchema={CourseCodeValidationSchema}
                          disabled={!isStateAdmin()}
                        />
                      </div>
                    </div>
                  ))}
                  <br />
                  {isStateAdmin() && (
                    <ActionButton
                      classes="button-specific-endorsement"
                      onClick={() => push({ endorsements: [] })}
                      tooltipText={'Add New Course Code Combinations'}
                    >
                      {'+ Add Specific Endorsements'}
                    </ActionButton>
                  )}
                </div>
                {deleteIndex !== -1 && (
                  <Modal
                    type={DialogType.CONFIRM}
                    open={deleteIndex !== -1}
                    onClose={() => setDeleteIndex(-1)}
                    title={'Are you sure?'}
                  >
                    <div
                      className="confirm-container"
                      data-testid="confirmation-modal"
                    >
                      <p data-testid="cancel-message">
                        &quot;Delete Specific Endorsements? Are you sure you
                        want to delete this specific endorsement
                        combination?&quot; Yes, delete, No, cancel.
                      </p>
                      <div className="button-row">
                        <ActionButton
                          onClick={() => {
                            remove(deleteIndex);
                            setDeleteIndex(-1);
                          }}
                          dataTestId="yes-button"
                          tooltipText={'Navigate to the last page'}
                        >
                          <span>Yes</span>
                        </ActionButton>
                        <ActionButton
                          classes="button--secondary"
                          onClick={() => setDeleteIndex(-1)}
                          dataTestId="no-button"
                          tooltipText={'Stay on existing page'}
                        >
                          <span>No</span>
                        </ActionButton>
                      </div>
                    </div>
                  </Modal>
                )}
              </>
            );
          }}
        </FieldArray>
      </div>
    </Panel>
  );
};

export default CourseCodeCertificationAndFundingPanel;
